import { createRouter, createWebHistory } from "vue-router";

// pages
import ActivityMetrics from "@/pages/ActivityMetrics";
import BaseTagManager from "@/pages/BaseTagManager";
import DactylAccessDenied from "@/pages/DactylAccessDenied";
import DactylDashboard from "@/pages/DactylDashboard";
import DataEntry from "@/pages/DataEntry";
import DataViewer from "@/pages/DataViewer";
import CompletedExtractions from "@/pages/CompletedExtractions";
import EmptyRouter from "@/pages/EmptyRouter";
import DactylHome from "@/pages/DactylHome";
import PaperManager from "@/pages/PaperManager";
import PasswordReset from "@/pages/PasswordReset";
import MagicLogin from "@/pages/MagicLogin";
import ProjectManager from "@/pages/ProjectManager";
import UserSettings from "@/pages/UserSettings";
import Organizations from "@/pages/Organizations";
import TagGroupManager from "@/pages/TagGroupManager";
import TrainingDashboard from "@/pages/TrainingDashboard";
import UserManager from "@/pages/UserManager";
import UserManual from "@/pages/UserManual";
import DactylWelcome from "@/pages/DactylWelcome";
import MetathesaurusBrowser from "@/pages/MetathesaurusBrowser";
import PreferredUnits from "@/pages/PreferredUnits";
import RawDataViewer from "@/pages/RawDataViewer";
import SsoRedirect from "@/pages/SsoRedirect";

import { PROSPECT_HOME_PATH } from "@/constants";

// interview management pages (lazy-loaded)
const InterviewBatchList = () =>
	import("@/pages/InterviewManagement/InterviewBatchList.vue");
const InterviewBatchCreate = () =>
	import("@/pages/InterviewManagement/InterviewBatchCreate.vue");
const InterviewBatchDetail = () =>
	import("@/pages/InterviewManagement/InterviewBatchDetail.vue");

// project management pages (DCTL-1446 / DCTL-1447, lazy-loaded). Flag-gated at
// the nav + backend (404 when project-dashboard is off); the route itself is
// permission-gated like every other /home route.
const ProjectManagementDashboard = () =>
	import("@/pages/ProjectManagement/Dashboard.vue");
const ProjectManagementView = () =>
	import("@/pages/ProjectManagement/ProjectView.vue");

// Systematic Literature Search (DCTL-2543, epic DCTL-1064, lazy-loaded).
// Flag-gated at the nav + backend (every /systematic-literature-search endpoint
// 404s while the systematic-literature-search LD flag is off); the routes
// themselves are permission-gated like every other /home route.
const SystematicLiteratureSearchSessionList = () =>
	import("@/pages/SystematicLiteratureSearch/SessionList.vue");
const SystematicLiteratureSearchSessionView = () =>
	import("@/pages/SystematicLiteratureSearch/SessionView.vue");

const routes = [
	{
		path: "/",
		component: DactylWelcome,
		meta: {
			guest: true,
		},
		props(route) {
			const fromPath = route.redirectedFrom?.path;
			if (fromPath) {
				if (fromPath.startsWith("/login")) {
					if (route.query.token) {
						return { token: route.query.token };
					} else {
						return { login: true };
					}
				} else if (fromPath.startsWith("/sso/failure")) {
					return { ssoFailure: true };
				}
			}

			return {};
		},
	},
	{
		path: "/login",
		redirect: "/",
	},
	{
		path: "/sso/failure",
		redirect: "/",
	},
	{
		path: "/sso-redirect",
		component: SsoRedirect,
		meta: {
			guest: true,
			hideChrome: true,
		},
	},
	{
		path: "/password-reset/:token",
		component: PasswordReset,
		meta: {
			guest: true,
		},
	},
	{
		// Prospect passwordless login landing page (DCTL-1927). The magic link
		// email points here; this page validates the signed URL and drives the
		// existing /login token-path. No auth required to view.
		path: "/magic-login/:token",
		component: MagicLogin,
		meta: {
			guest: true,
		},
	},
	{
		// Provenance Viewer (DCTL-1842): the destination of the XLSX cell
		// hyperlinks. Full-bleed, no app chrome; entitlement is enforced by the
		// API, so no permission meta here (external client users have none).
		path: "/provenance-viewer/:qcUuid/:studyIndex/:dpUuid",
		component: () => import("@/pages/ProvenanceViewer"),
		meta: {
			requireAuth: true,
			hideChrome: true,
		},
	},
	{
		// Getting-started landing for a contained prospect (DCTL-1931) who signs
		// in without a specific destination (e.g. via "Login via Magic Link"
		// rather than an example-data link). Full-bleed, no app chrome; the nav
		// guard steers prospects here instead of the forbidden home dashboard.
		path: PROSPECT_HOME_PATH,
		component: () => import("@/pages/ProspectWelcome"),
		meta: {
			requireAuth: true,
			hideChrome: true,
		},
	},
	{
		path: "/home",
		component: DactylHome,
		meta: {
			requireAuth: true,
		},
		children: [
			{
				path: "user-manager",
				component: UserManager,
				meta: {
					requireAuth: true,
					permissions: ["create_user", "edit_user", "delete_user"],
				},
			},
			{
				path: "activity-metrics",
				component: ActivityMetrics,
				meta: {
					requireAuth: true,
					permissions: ["view_activity_metrics"],
				},
			},
			{
				path: "dashboard",
				component: DactylDashboard,
				meta: {
					requireAuth: true,
				},
			},
			{
				path: "training-dashboard",
				component: TrainingDashboard,
				meta: {
					requireAuth: true,
					permissions: ["sde_training"],
				},
			},
			{
				path: "quality-control-dashboard",
				component: () => import("@/pages/QCDashboard"),
				meta: {
					requireAuth: true,
				},
			},
			{
				path: "quality-control-verified",
				component: () => import("@/pages/VerifiedData.vue"),
				meta: {
					requireAuth: true,
					permissions: ["quality_control"],
				},
			},
			{
				path: "completed-extractions",
				component: CompletedExtractions,
				meta: {
					requireAuth: true,
					permissions: ["access_completed_extractions"],
				},
				props: () => ({
					mode: "all",
				}),
			},
			{
				path: "my-completed-extractions",
				component: CompletedExtractions,
				meta: {
					requireAuth: true,
					permissions: ["source_data_extraction"],
				},
				props: () => ({
					mode: "mine",
				}),
			},
			{
				// DCTL-2048 (Nav IA Phase 4): behind project-dashboard this route's
				// component renders the orphaned-papers list, gated on manage_projects
				// (admin + group_lead + paper_manager). The guard is widened from the
				// legacy delete_file/upload_file to manage_projects so group leads (who
				// hold manage_projects but not upload_file) can reach the orphan list;
				// paper managers hold both, so flag-off access to the legacy table is
				// preserved.
				path: "paper-manager",
				component: PaperManager,
				meta: {
					requireAuth: true,
					permissions: ["manage_projects"],
				},
			},
			{
				path: "project-manager",
				component: ProjectManager,
				meta: {
					requireAuth: true,
					permissions: ["delete_file", "upload_file"],
				},
			},
			{
				// DCTL-1446: manager all-projects dashboard.
				//
				// DCTL-2484: `anyPermissions` (OR), not `permissions` (AND), so a
				// download_data holder can reach the project view that hosts the
				// verified-data export (DCTL-2482). No user holds manage_projects
				// without download_data, so this grants the page to nobody who did
				// not already hold a project permission. Every project-management
				// control on both screens carries its own manage_projects gate.
				path: "project-management",
				component: ProjectManagementDashboard,
				meta: {
					requireAuth: true,
					anyPermissions: ["manage_projects", "download_data"],
				},
			},
			{
				// DCTL-1447: manager per-project view. DCTL-2484: see the dashboard
				// route above for why this is `anyPermissions`.
				path: "project-management/:uuid",
				component: ProjectManagementView,
				meta: {
					requireAuth: true,
					anyPermissions: ["manage_projects", "download_data"],
				},
			},
			{
				// DCTL-2543: PICOS-driven literature discovery (design doc
				// docs/design-systematic-literature-search.md). The list is the
				// entry point; a session view hosts criteria -> plan -> screening
				// -> PRISMA.
				path: "systematic-literature-search",
				component: SystematicLiteratureSearchSessionList,
				meta: {
					requireAuth: true,
					permissions: ["search_literature"],
				},
			},
			{
				path: "systematic-literature-search/new",
				component: SystematicLiteratureSearchSessionView,
				meta: {
					requireAuth: true,
					permissions: ["search_literature"],
				},
			},
			{
				path: "systematic-literature-search/:uuid",
				component: SystematicLiteratureSearchSessionView,
				meta: {
					requireAuth: true,
					permissions: ["search_literature"],
				},
				props: true,
			},
			{
				path: "organizations",
				component: Organizations,
				meta: {
					requireAuth: true,
				},
			},
			{
				// DCTL-2042: "Metathesaurus" renamed to "Dactyl Concepts". The
				// component is already the Concepts browser; this is a label + path
				// rename. The old path redirects so existing links keep working.
				path: "dactyl-concepts",
				component: MetathesaurusBrowser,
				meta: {
					requireAuth: true,
					permissions: ["access_completed_extractions"],
				},
			},
			{
				path: "metathesaurus",
				redirect: "/home/dactyl-concepts",
			},
			{
				// DCTL-2191: Preferred Units admin page. Whole page gated on
				// manage_units (admins bypass, group_lead holds the grant); QC
				// users do not see it. Mutations are the same gate on the backend
				// + via store.can in the page.
				path: "preferred-units",
				component: PreferredUnits,
				meta: {
					requireAuth: true,
					permissions: ["manage_units"],
				},
			},
			{
				path: "settings",
				component: UserSettings,
				meta: {
					requireAuth: true,
				},
			},
			{
				path: "tag",
				component: EmptyRouter, // this is needed for vue-router v3
				children: [
					{
						path: "base-tag-manager",
						component: BaseTagManager,
						meta: {
							requireAuth: true,
							permissions: ["manage_tags"],
						},
					},
					{
						path: "tag-group-manager",
						component: TagGroupManager,
						meta: {
							requireAuth: true,
							permissions: ["manage_tags"],
						},
					},
				],
			},
			{
				path: "interview-batches",
				component: InterviewBatchList,
				meta: {
					requireAuth: true,
					permissions: ["manage_interviews"],
				},
			},
			{
				path: "interview-batches/create",
				component: InterviewBatchCreate,
				meta: {
					requireAuth: true,
					permissions: ["manage_interviews"],
				},
			},
			{
				path: "interview-batches/:uuid",
				component: InterviewBatchDetail,
				meta: {
					requireAuth: true,
					permissions: ["manage_interviews"],
				},
				props: (route) => ({
					batchUuid: route.params.uuid,
				}),
			},
		],
	},
	{
		path: "/data-entry/:paperUuid/:sdeUuid/:dataPointUuid?",
		component: DataEntry,
		meta: {
			requireAuth: true,
		},
		props: (route) => ({
			paperUuid: route.params.paperUuid,
			sdeUuid: route.params.sdeUuid,
			dataPointUuid: route.params.dataPointUuid,
		}),
	},
	{
		path: "/data-entry-training/:paperUuid/:sdeUuid/:dataPointUuid?",
		component: DataEntry,
		meta: {
			requireAuth: true,
		},
		props: (route) => ({
			paperUuid: route.params.paperUuid,
			sdeUuid: route.params.sdeUuid,
			dataPointUuid: route.params.dataPointUuid,
			training: true,
		}),
	},
	{
		path: "/data-entry-read-only/:paperUuid/:sdeUuid/:dataPointUuid?",
		component: DataEntry,
		meta: {
			requireAuth: true,
		},
		props: (route) => ({
			paperUuid: route.params.paperUuid,
			sdeUuid: route.params.sdeUuid,
			dataPointUuid: route.params.dataPointUuid,
			readOnly: true,
		}),
	},
	{
		path: "/sde-user-manual",
		component: () => import("@/pages/SdeUserManual"),
		meta: {
			requireAuth: true,
		},
	},
	{
		path: "/qc-user-manual",
		component: () => import("@/pages/QcUserManual"),
		meta: {
			requireAuth: true,
			permissions: ["quality_control"],
		},
	},
	{
		path: "/source-data-extraction/:paperUuid/:sdeUuid",
		component: () => import("@/pages/SourceDataExtraction"),
		meta: {
			requireAuth: true,
		},
		props: true,
	},
	{
		path: "/source-data-extraction/:paperUuid/:sdeUuid/view",
		component: () => import("@/pages/SourceDataExtractionViewer"),
		meta: {
			requireAuth: true,
			// Standalone full-viewport tool: the page renders its own header
			// with refresh + layout toggle, so the global Dactyl app bar would
			// just clutter the chrome. Save / Validate buttons in particular
			// would be misleading in a read-only viewer.
			hideChrome: true,
		},
		props: true,
	},
	{
		// "View current file" for a ClinicalTrials.gov registry document
		// (DCTL-2755): the SDEv2 renderer, chip-free, in its own tab. A PDF
		// opens in the browser's own viewer instead; raw JSON would not read.
		path: "/papers/:paperUuid/files/:fileUuid/view",
		component: () => import("@/pages/RegistryDocumentViewer"),
		meta: {
			requireAuth: true,
			hideChrome: true,
		},
		props: true,
	},
	{
		path: "/source-data-extraction-training/:paperUuid/:sdeUuid",
		component: () => import("@/pages/SourceDataExtraction"),
		meta: {
			requireAuth: true,
		},
		props: (route) => ({
			paperUuid: route.params.paperUuid,
			sdeUuid: route.params.sdeUuid,
			training: true,
		}),
	},
	{
		path: "/source-data-extraction-read-only/:paperUuid/:sdeUuid",
		component: () => import("@/pages/SourceDataExtraction"),
		meta: {
			requireAuth: true,
		},
		props: (route) => ({
			paperUuid: route.params.paperUuid,
			sdeUuid: route.params.sdeUuid,
			readOnly: true,
		}),
	},
	{
		path: "/data-viewer",
		component: DataViewer,
		meta: {
			requireAuth: true,
			permissions: ["access_data_viewer"],
		},
		props: (route) => ({
			paperUuid: route.query.paperUuid || null,
			sdeUuid: route.query.sdeUuid || null,
			dataPointUuid: route.query.dataPointUuid || null,
		}),
	},
	{
		path: "/quality-control/:qcUuid",
		component: () => import("@/pages/QualityControl/index.vue"),
		meta: {
			requireAuth: true,
			permissions: ["quality_control"],
		},
		props: true,
	},
	{
		path: "/user-manual",
		component: UserManual,
		meta: {
			requireAuth: true,
		},
	},
	{
		path: "/access-denied",
		component: DactylAccessDenied,
		meta: {
			requireAuth: true,
		},
	},
	{
		path: "/raw-data-viewer",
		component: RawDataViewer,
		meta: {
			requireAuth: true,
		},
	},
	// TODO make a cheeky 404 page
	// {
	// 	path: "/:pathMatch(.*)*",
	// 	redirect: "/",
	// },
];

export default createRouter({
	history: createWebHistory(),
	routes,
});
